import { Bell } from "lucide-react";

import { CountBadge } from "@/components/shared/count-badge";
import { cn } from "@/lib/utils";

const ALERTS_BUTTON_BASE =
  "inline-flex items-center justify-center gap-1.5 font-medium h-7 px-2.5 text-xs transition-colors hover:bg-muted hover:text-muted-foreground";

export const ALERTS_BUTTON_VARIANTS = {
  compact: "compact",
  full: "full",
} as const;

export type AlertsButtonVariant =
  (typeof ALERTS_BUTTON_VARIANTS)[keyof typeof ALERTS_BUTTON_VARIANTS];

export const ALERTS_STATUS = {
  on: "on",
  off: "off",
  unavailable: "unavailable",
} as const;

export type AlertsStatus = (typeof ALERTS_STATUS)[keyof typeof ALERTS_STATUS];

const ALERTS_BUTTON_LABEL: { [K in AlertsStatus]: string } = {
  on: "Alerts",
  off: "Get alerts",
  unavailable: "Get alerts",
};

const alertsAriaLabel = (status: AlertsStatus, activeCount: number): string => {
  if (status === ALERTS_STATUS.on) return `Job alerts, ${activeCount} active`;
  if (status === ALERTS_STATUS.unavailable) {
    return "Job alerts unavailable, try again";
  }
  return "Get job alerts";
};

export interface AlertsTriggerButtonProps {
  variant: AlertsButtonVariant;
  status: AlertsStatus;
  activeCount: number;
  testId: string;
  onClick: () => void;
}

const AlertsCountBadge = ({ count }: { count: number }) => {
  if (count <= 0) {
    return null;
  }

  return (
    <span
      className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full leading-none bg-foreground text-background"
      data-testid="alerts-count-badge"
    >
      {count}
    </span>
  );
};

export const AlertsTriggerButton = ({
  variant,
  status,
  activeCount,
  testId,
  onClick,
}: AlertsTriggerButtonProps) => {
  const isOn = status === ALERTS_STATUS.on;
  const isCompact = variant === ALERTS_BUTTON_VARIANTS.compact;
  const label = ALERTS_BUTTON_LABEL[status];
  const ariaLabel = alertsAriaLabel(status, activeCount);

  return (
    <div className="border rounded-lg p-0.5 bg-surface-subtle">
      <button
        className={cn("relative", ALERTS_BUTTON_BASE)}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        <Bell
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            isOn && "fill-current scale-110 text-foreground",
          )}
        />
        {!isCompact && <span data-testid="alerts-text">{label}</span>}
        {isOn && isCompact && (
          <span
            data-testid="compact-alerts-count-badge"
            className="absolute -top-1 -right-1"
          >
            <CountBadge count={activeCount} className="ml-0" />
          </span>
        )}
        {isOn && !isCompact && <AlertsCountBadge count={activeCount} />}
      </button>
    </div>
  );
};
